import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Divider, Form, Skeleton } from 'antd';
import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';
import { PageProps } from '@/types/PageProps';
import WorkOrderComplementTabs from '@/components/Movements/WorkOrders/Details/Complements/WorkOrderComplementTabs';
import WorkOrderComplementForm, {
  WorkOrderComplementFormData,
} from '@/components/Movements/WorkOrders/Details/Complements/ComplementForm';
import WorkOrderDetailsCard from '@/components/Movements/WorkOrders/Details/WorkOrderDetailsCard';
import { useNotFoundMessage } from '@/hooks/useNotFoundMessage';
import ComplementHeader from '@/components/Movements/WorkOrders/Details/Complements/ComplementHeader';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import { useCompaniesByLoggedEmployee } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useEffect } from 'react';
import WorkOrderFooter from '@/components/Movements/WorkOrders/Details/WorkOrderFooter';
import { useWorkorderForms } from '@/components/Movements/WorkOrders/Details/WorkOrderFormProvider';
import WorkorderTabExtraAction from '@/components/Movements/WorkOrders/Details/WorkorderTabExtraAction';
import workOrderValidationTabs from '@/utils/functions/workOrderValidationTabs';
import WorkOrderComplementInvoice from '@/components/Movements/WorkOrders/Details/Complements/WorkOrderComplementInvoice';
import { isAppPortocar } from '@/index';
import WorkOrderPortoBatteryPayment from '@/components/Movements/WorkOrders/Details/WorkOrderPortoBatteryPayment';
import WorkOrderInfoSchedule from '@/components/Movements/WorkOrders/Details/WorkOrderInfoSchedule';

function WorkOrderGeneralInfoPage({ route }: PageProps) {
  const intl = useIntl();
  const { forms, formsValues, isLoading, hasFormChange } = useWorkorderForms();
  const [form] = Form.useForm<WorkOrderComplementFormData>(forms.generalInfo);

  const {
    workorder: { data: workOrder, isLoading: isLoadingWorkOrder },
    complement: {
      data: complement,
      isLoading: isLoadingComplement,
      complementIdentifier,
    },
    formatFormComplementData,
  } = useWorkorderDetailContext();

  const type = Form.useWatch('type', form);

  const displayNumber = `${workOrder?.number
    .toString()
    .padStart(6, '0')}/${complement?.number.toString().padStart(2, '0')}`;

  const notFountMessage = useNotFoundMessage('workOrders.domain.customer');

  useUpdateBreadcrumbParam(':workOrderId', displayNumber || notFountMessage);

  const { data: companiesData } = useCompaniesByLoggedEmployee();
  const companies = flatInfiniteData(companiesData);

  useEffect(() => {
    if (complement && workOrder) {
      if (
        !formsValues.generalInfo ||
        (!!formsValues.generalInfo &&
          form.getFieldValue('complementId') !== complement.id)
      ) {
        const complementFormData = formatFormComplementData();

        if (complementFormData) {
          form.setFieldsValue(complementFormData);
        }

        if (complement?.insurer?.id) {
          form.setFieldValue('insurerId', complement.insurer.id);
        }
      }
    }
  }, [complement, workOrder, companies, formsValues.generalInfo]);

  return (
    <>
      <Helmet
        title={`${
          complementIdentifier ? `${complementIdentifier} | ` : ''
        }${intl.formatMessage({
          id: route.name,
        })}`}
      />

      <WorkOrderComplementTabs
        route={workOrderValidationTabs(route, type, complement?.status)}
        permissions={route.permissions}
        isLoading={isLoadingWorkOrder || isLoadingComplement}
        extra={<WorkorderTabExtraAction />}
      >
        <ComplementHeader isGeneralData />

        <Skeleton
          loading={isLoading || isLoadingWorkOrder || isLoadingComplement}
        >
          <WorkOrderComplementForm form={form} workOrder={workOrder} />
        </Skeleton>

        {isAppPortocar && <WorkOrderPortoBatteryPayment />}

        <WorkOrderInfoSchedule />

        <Divider />

        <WorkOrderDetailsCard />

        <Divider />

        <WorkOrderComplementInvoice />

        <Divider />

        <WorkOrderFooter disabled={!hasFormChange} />
      </WorkOrderComplementTabs>
    </>
  );
}

export default WorkOrderGeneralInfoPage;
